// Interfaces
export interface SkillDistribution {
  skillName: string;
  employeeCount: number;
}

export interface MetricsData {
  totalNumberOfOpenPositions: number;
  averageSpeedOfClosure: number;
  countOfVoluntaryAttrition: number;
  countOfInvoluntaryAttrition: number;
  nicheSkillsCandidatesCount: number;
  bufferResourcesCount: number;
  numberOfFullTimeConversions: number;
}

export interface MetricsResponse {
  status: string;
  data: MetricsData;
}

export interface SkillsResponse {
  status: string;
  data: SkillDistribution[];
}

export interface MetricItem {
  label: string;
  value: number | string;
}

export interface MonthOption {
  label: string;
  value: number;
  selected?: boolean;
}

export interface QuarterOptions {
  label: string;
  value: number;
  year: string;
  quarter: string;
  selected?: boolean;
}

// Chart Configuration
export const CHART_CONFIG = {
  COLORS: {
    TEXT_PRIMARY: '#1A1A1A',
    TEXT_SECONDARY: '#6B7280'
  },
  LAYOUT: {
    PADDING: 50,
    CUTOUT: '60%',
    RADIUS: '80%'
  },
  DATALABELS: {
    FONT_SIZE: {
      LABEL: 14,
      VALUE: 12
    },
    OFFSET: 15,
    MAX_LINE_LENGTH: 10
  }
};

// Chart Helper Functions
export class ChartHelper {
  public static formatLabel(label: string, maxLength: number = CHART_CONFIG.DATALABELS.MAX_LINE_LENGTH): string {
    if (!label) return '';
    return label.length > maxLength ? 
      label.match(new RegExp(`.{1,${maxLength}}(\\s|$)`, 'g'))?.join('\n') || label : 
      label;
  }

  public static getChartColors(): string[] {
    const style = getComputedStyle(document.documentElement);
    const chartColorsVar = style.getPropertyValue('--chart-colors').trim();
    return chartColorsVar.split(',').map(color => color.trim());
  }

  public static getQuarter(month: number): string {
    if (month >= 1 && month <= 3) return 'Q1';
    if (month >= 4 && month <= 6) return 'Q2';
    if (month >= 7 && month <= 9) return 'Q3';
    return 'Q4';
  }

  public static toProperCase(text: string): string {
    if (!text) return text;

    const isAllCaps = text.replace(/[^a-zA-Z]/g, '').toUpperCase() === text.replace(/[^a-zA-Z]/g, '');

    if (isAllCaps && text.length > 1) {
      return text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    }

    return text;
  }

  public static wrapText(text: string, maxLength: number = PEOPLE_METRICS.CHART.MAX_LABEL_LENGTH): string[] {
    if (!text) return [''];

    if (text.length <= maxLength) return [text];

    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach((word) => {
      if (word.length > maxLength) {
        if (currentLine) {
          lines.push(currentLine.trim());
          currentLine = '';
        }

        for (let i = 0; i < word.length; i += maxLength) {
          const chunk = word.substring(i, i + maxLength);
          lines.push(chunk);
        }
      } else {
        const testLine = currentLine ? `${currentLine} ${word}` : word;

        if (testLine.length <= maxLength) {
          currentLine = testLine;
        } else {
          if (currentLine) {
            lines.push(currentLine.trim());
          }
          currentLine = word;
        }
      }
    });

    if (currentLine) {
      lines.push(currentLine.trim());
    }

    return lines.length > 0 ? lines : [''];
  }

  public static getDeviceType(): { isMobile: boolean; isSmallMobile: boolean } {
    const width = window.innerWidth;
    return {
      isMobile: width <= PEOPLE_METRICS.BREAKPOINTS.MOBILE,
      isSmallMobile: width <= PEOPLE_METRICS.BREAKPOINTS.SMALL_MOBILE
    };
  }
}

export const PEOPLE_METRICS = {
  LABELS: {
    POSITIONS: 'Number of\nPositions',
    CLOSURE_SPEED: 'Speed of\nClosures -Days',
    VOLUNTARY_ATTRITION: 'Voluntary\nAttrition',
    INVOLUNTARY_ATTRITION: 'Non -Voluntary\nAttrition',
    NICHE_SKILLS: 'No of Niche\nSkills',
    BUFFER_RESOURCES: 'Buffer\nResources',
    CTH: 'YTD CTH'
  },
  IMAGES: {
    NO_DATA: 'assets/img/no-data-found.png'
  },
  MESSAGES: {
    INVALID_MONTH_SKILLS: 'Invalid month selected for skills section.',
    INVALID_MONTH_METRICS: 'Invalid month selected for metrics section.',
    LOADING_SKILLS: 'Loading skills data...',
    LOADING_METRICS: 'Loading metrics data...',
    EMPLOYEE_AUTH_ERROR: 'Employee authentication not available. Please log in again.',
    VENDOR_ID_ERROR: 'Vendor information not found in URL parameters.',
    MISSING_IDS_ERROR: 'Missing employee or vendor ID. Please check your authentication and route parameters.',
    API_SUCCESS_STATUS: 'SUCCESS'
  },
  CHART: {
    TOP_SKILLS_LIMIT: 10,
    CURRENT_MONTH_INDEX: 0,
    MAX_LABEL_LENGTH: 12,
    WRAP_TEXT_LENGTH: 8,
    FONT_SIZE: {
      DESKTOP: 12,
      MOBILE: 12,
      SMALL_MOBILE: 14
    },
    PADDING: {
      DESKTOP: 70,
      MOBILE: 60,
      SMALL_MOBILE: 50
    },
    LABEL_OFFSET: {
      DESKTOP: 15,
      MOBILE: 14,
      SMALL_MOBILE: 12
    },
    RADIUS: {
      DESKTOP: '95%',
      MOBILE: '92%',
      SMALL_MOBILE: '90%'
    },
    CUTOUT: {
      DESKTOP: '60%',
      MOBILE: '58%',
      SMALL_MOBILE: '55%'
    },
    DEFAULT_COLORS: [
      'var(--chart-dark-blue)',
      'var(--chart-royal-purple)',
      'var(--chart-slate-gray)',
      'var(--chart-steel-blue)',
      'var(--chart-sage-green)',
      'var(--chart-plum)',
      'var(--chart-rust-brown)',
      'var(--chart-terra-cotta)',
      'var(--chart-steel-blue-alt)',
      'var(--chart-purple-alt)',
      'var(--chart-gray-alt)',
      'var(--chart-sage-light)',
      'var(--chart-mauve)',
      'var(--chart-brown)'
    ]
  },
  TIME: {
    MONTHS_TO_SHOW: 12
  },
  BREAKPOINTS: {
    MOBILE: 768,
    SMALL_MOBILE: 480
  },
  ROUTE_PARAMS: {
    VENDOR_ID: ['vendorId', 'id'],
    QUERY_VENDOR_ID: ['vendorId', 'vendor']
  },
  ORG_LEVEL_MESSAGE: 'This is org level data by Vendor'
};
